import {
  Controller,
  Control,
  FieldPath,
  RegisterOptions,
  FieldValues,
} from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Stack, styled, Typography } from '@mui/material';
import { useId } from 'react';
import { theme } from '~/shared/styles/theme';

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder: string;
  rules?: RegisterOptions<T>;
  required?: boolean;
  width?: string | number;
  backgroundColor?: string;
  birthDate?: Date | null;
}

const FormDatePicker = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  rules,
  required = false,
  width = '100%',
  backgroundColor,
  birthDate,
}: Props<T>) => {
  const labelId = useId();

  const validateDate = (value: string | Date | null | undefined) => {
    if (!value) {
      return true;
    }

    const dateValue = typeof value === 'string' ? new Date(value) : value;

    if (isNaN(dateValue.getTime())) {
      return `${label} must be a valid date`;
    }

    if (dateValue > new Date()) {
      return `${label} cannot be in the future`;
    }

    if (dateValue.getFullYear() < 1) {
      return `${label} must be year 1 or later`;
    }

    if (name === 'deathDate' && birthDate && dateValue < new Date(birthDate)) {
      return 'Death date cannot be before birth date';
    }

    return true;
  };

  const getExistingValidation = () => {
    if (typeof rules?.validate === 'object') {
      return rules.validate;
    }

    if (rules?.validate) {
      return { custom: rules.validate };
    }

    return {};
  };

  const finalRules: RegisterOptions<T> = {
    ...rules,
    required:
      rules?.required ?? (required ? `${label} is required` : undefined),
    validate: {
      ...getExistingValidation(),
      dateValidation: validateDate,
    },
  };

  return (
    <Stack width="100%">
      <Label
        id={labelId}
        variant="body2"
        color={theme.palette.custom.textTitle}
      >
        {label}
        {required && '*'}
      </Label>
      <Controller
        name={name}
        control={control}
        rules={finalRules}
        render={({ field, fieldState }) => {
          let dateValue: Date | null = null;

          if (field.value) {
            if (typeof field.value === 'string') {
              dateValue = new Date(field.value);
            } else {
              dateValue = field.value;
            }
          }

          return (
            <DatePicker
              value={dateValue}
              onChange={field.onChange}
              slotProps={{
                textField: {
                  placeholder,
                  fullWidth: true,
                  error: Boolean(fieldState.error),
                  helperText: fieldState.error?.message,
                  InputProps: {
                    'aria-labelledby': labelId,
                    sx: {
                      height: '40px',
                      width,
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: theme.palette.custom.lightBrown,
                      ...(backgroundColor && { backgroundColor }),
                      '& fieldset': {
                        borderColor: theme.palette.custom.lightGrayBrown,
                      },
                      '&:hover fieldset': {
                        borderColor: `${theme.palette.custom.lightGrayBrown} !important`,
                      },
                    },
                  },
                  sx: {
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '14px',
                      color: theme.palette.custom.lightBrown,
                    },
                  },
                },
              }}
            />
          );
        }}
      />
    </Stack>
  );
};

export default FormDatePicker;

const Label = styled(Typography)({
  marginBottom: theme.spacing(1),
});
